import Close from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BackButton, PrimaryButton } from 'components/forms/atoms/FormButtons';
import FormField from 'components/forms/atoms/FormField';
import InlineFeedback from 'components/forms/molecules/InlineFeedback';
import { useFeedback } from 'contexts/FeedbackContext';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetAdminBusinessUnitsQuery } from 'services/api/adminBusinessUnits.api';
import { useCreateConversationEvalMutation } from 'services/api/conversationEvals.api';
import type { Client } from 'services/types/client';
import { GLASS_BACKGROUND, GLASS_BLUR, NAVY, RADIUS_ITEM, SURFACE_CONTAINER_LOW, YELLOW } from 'styles/tokens';

import { useEvalSource } from '../hooks/useEvalSource';
import type { PlaygroundTurn, TurnRole } from '../types';
import { defaultCheckpointUid, turnCallsTools, turnsToEvalMessages } from '../utils/turnMapping';

interface CreateEvalPanelProps {
  open: boolean;
  onClose: () => void;
  /** Thread to store, already including the response the eval grades against. */
  turns: PlaygroundTurn[];
  businessUnitId: number | null;
  /** Source conversation, when this session was opened from a real message. */
  messageId?: string;
  /** Continuation after saving: grade the stored cases right away. */
  onRunEvals: () => void;
}

const sectionLabel = {
  fontFamily: 'Jost, sans-serif',
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: alpha(NAVY, 0.5),
};

const bodyText = {
  fontFamily: 'Jost, sans-serif',
  fontSize: 13,
  color: NAVY,
};

const ROLE_LABELS: Record<TurnRole, string> = {
  system: 'Sistema',
  user: 'Cliente',
  assistant: 'Asistente',
  tool: 'Herramienta',
};

const describeTurn = (turn: PlaygroundTurn): string => {
  const calls = (turn.toolCalls ?? []).map((call) => `${call.name}(${call.arguments})`).join('\n');
  return [turn.content, calls].filter(Boolean).join('\n') || 'Sin contenido';
};

const CreateEvalPanel: FC<CreateEvalPanelProps> = ({ open, onClose, turns, businessUnitId, messageId, onRunEvals }) => {
  const { notifySaved } = useFeedback();
  const navigate = useNavigate();
  const { data: businessUnits } = useGetAdminBusinessUnitsQuery(null, { skip: !open });
  const source = useEvalSource(open, messageId, businessUnitId);
  const [createConversationEval, { isLoading: saving }] = useCreateConversationEvalMutation();

  /** Description per checkpoint, keyed by turn uid. A key present means the turn is marked. */
  const [checkpoints, setCheckpoints] = useState<Record<string, string>>({});
  const [businessUnitIds, setBusinessUnitIds] = useState<number[]>([]);
  const [mockClientJson, setMockClientJson] = useState('{}');
  const [clientOpen, setClientOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedEvalId, setSavedEvalId] = useState<number | null>(null);

  // Only the outcome flags reset on open; what the user typed survives closing the panel by accident.
  useEffect(() => {
    if (!open) {
      return;
    }
    setSubmitAttempted(false);
    setSaveError(null);
    setSavedEvalId(null);
  }, [open]);

  // Seed the last assistant turn as the checkpoint, unless the author already picked their own.
  useEffect(() => {
    const uid = defaultCheckpointUid(turns);
    setCheckpoints((current) => (Object.keys(current).length > 0 || !uid ? current : { [uid]: '' }));
  }, [turns]);

  // Prefill from the source conversation as it resolves. Both setters take a fresh reference per fetch,
  // so a later edit by the user is not overwritten on re-render.
  useEffect(() => {
    setBusinessUnitIds(source.businessUnitIds);
  }, [source.businessUnitIds]);

  useEffect(() => {
    setMockClientJson(JSON.stringify(source.mockClient, null, 2));
  }, [source.mockClient]);

  const dialogue = useMemo(() => turns.filter((turn) => turn.role !== 'system'), [turns]);
  const describedCount = Object.values(checkpoints).filter((description) => description.trim()).length;

  const parsedMockClient = useMemo(() => {
    try {
      return { value: JSON.parse(mockClientJson) as Partial<Client>, valid: true };
    } catch {
      return { value: {} as Partial<Client>, valid: false };
    }
  }, [mockClientJson]);

  const missingFields = useMemo(() => {
    const fields: string[] = [];
    if (describedCount === 0) {
      fields.push('Un checkpoint con descripción');
    }
    if (businessUnitIds.length === 0) {
      fields.push('Business units');
    }
    if (!parsedMockClient.valid) {
      fields.push('Datos del cliente');
    }
    return fields;
  }, [describedCount, businessUnitIds, parsedMockClient.valid]);

  const toggleCheckpoint = (uid: string) => {
    setCheckpoints((current) => {
      if (uid in current) {
        const { [uid]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [uid]: '' };
    });
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    setSaveError(null);
    if (missingFields.length > 0) {
      return;
    }
    try {
      const created = await createConversationEval({
        businessUnitIds,
        mockClient: parsedMockClient.value,
        messages: turnsToEvalMessages(turns, checkpoints),
      }).unwrap();
      notifySaved('Eval creado');
      setCheckpoints({});
      setSavedEvalId(created.id);
    } catch {
      setSaveError('No pudimos guardar el eval. Revisa los datos e inténtalo otra vez.');
    }
  };

  const handleClose = () => {
    setSavedEvalId(null);
    onClose();
  };

  const showMissing = submitAttempted && missingFields.length > 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: 'min(560px, 100%)',
            background: GLASS_BACKGROUND,
            backdropFilter: GLASS_BLUR,
            display: 'flex',
            flexDirection: 'column',
            p: 2.5,
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FactCheckOutlined sx={{ fontSize: 20, color: NAVY }} />
          <Typography sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700, fontSize: 16, color: NAVY }}>
            Crear eval
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: alpha(NAVY, 0.6) }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Typography sx={{ ...bodyText, fontSize: 12.5, color: alpha(NAVY, 0.55), mb: 2 }}>
        Marca los puntos de la conversación que quieres evaluar. Cada uno se corre por separado: el asistente recibe el
        diálogo hasta ahí y comparamos su respuesta con lo que describas.
      </Typography>

      {savedEvalId !== null ? (
        <>
          <Box sx={{ flex: 1 }}>
            <InlineFeedback severity="success" sx={{ mb: 2 }}>
              Guardamos el eval <strong>#{savedEvalId}</strong> en{' '}
              {businessUnitIds.length === 1 ? 'una unidad' : `${businessUnitIds.length} unidades`}. Ya puedes correrlo
              contra el prompt que tengas en pantalla.
            </InlineFeedback>
            <Typography sx={{ ...bodyText, fontSize: 12.5, color: alpha(NAVY, 0.55) }}>
              Correrlo ahora te dice si las descripciones que escribiste califican como esperabas.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, pt: 2 }}>
            <BackButton onClick={() => navigate(`/conversation-evals?evalId=${savedEvalId}`)}>
              Ver en la lista
            </BackButton>
            <PrimaryButton
              onClick={() => {
                handleClose();
                onRunEvals();
              }}
            >
              Correr evals
            </PrimaryButton>
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {showMissing && (
              <InlineFeedback severity="error">
                Falta completar: <strong>{missingFields.join(', ')}</strong>.
              </InlineFeedback>
            )}
            {saveError && <InlineFeedback severity="warning">{saveError}</InlineFeedback>}
            {source.failed && (
              <InlineFeedback severity="warning">
                No pudimos cargar los datos del cliente. Complétalos abajo antes de guardar.
              </InlineFeedback>
            )}

            <Box>
              <Typography sx={{ ...sectionLabel, mb: 1 }}>
                Conversación · {describedCount} {describedCount === 1 ? 'checkpoint' : 'checkpoints'}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {dialogue.map((turn) => {
                  const marked = turn.uid in checkpoints;
                  const callsTools = turnCallsTools(turn);
                  return (
                    <Box
                      key={turn.uid}
                      sx={{
                        bgcolor: SURFACE_CONTAINER_LOW,
                        borderRadius: RADIUS_ITEM,
                        p: 1.25,
                        ...(marked ? { boxShadow: `inset 3px 0 0 ${YELLOW}` } : {}),
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={sectionLabel}>{ROLE_LABELS[turn.role]}</Typography>
                        {turn.role === 'assistant' && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={marked}
                                onChange={() => toggleCheckpoint(turn.uid)}
                                sx={{ color: alpha(NAVY, 0.35), '&.Mui-checked': { color: NAVY }, p: 0.5 }}
                              />
                            }
                            label="Evaluar aquí"
                            slotProps={{ typography: { sx: { ...bodyText, fontSize: 12 } } }}
                            sx={{ mr: 0 }}
                          />
                        )}
                      </Box>

                      <Typography
                        sx={{
                          ...bodyText,
                          fontSize: callsTools ? 11.5 : 12.5,
                          fontFamily: callsTools ? 'ui-monospace, monospace' : 'Jost, sans-serif',
                          whiteSpace: 'pre-wrap',
                          mt: 0.25,
                        }}
                      >
                        {describeTurn(turn)}
                      </Typography>

                      {marked && (
                        <Box sx={{ mt: 1.25 }}>
                          <FormField
                            label="¿Qué esperas acá?"
                            multiline
                            minRows={2}
                            value={checkpoints[turn.uid]}
                            onChange={(e) => setCheckpoints((current) => ({ ...current, [turn.uid]: e.target.value }))}
                            error={
                              submitAttempted && !checkpoints[turn.uid].trim()
                                ? 'Descríbelo o desmarca el punto.'
                                : null
                            }
                            hint={
                              callsTools
                                ? 'Compara la tool y sus argumentos, literal y sin juez: la descripción solo documenta.'
                                : 'Un juez compara la respuesta del asistente contra esta descripción.'
                            }
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <FormField
              select
              label="¿En qué business units corre?"
              value={businessUnitIds}
              onChange={(e) => setBusinessUnitIds(e.target.value as unknown as number[])}
              error={submitAttempted && businessUnitIds.length === 0 ? 'Elige al menos una.' : null}
              hint={messageId ? 'Prellenado con la unidad de la conversación original.' : undefined}
              slotProps={{
                select: {
                  multiple: true,
                  renderValue: (selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          label={businessUnits?.find((unit) => unit.id === id)?.name ?? id}
                          sx={{
                            bgcolor: alpha(NAVY, 0.06),
                            color: NAVY,
                            fontFamily: 'Jost, sans-serif',
                            borderRadius: RADIUS_ITEM,
                          }}
                        />
                      ))}
                    </Box>
                  ),
                },
              }}
            >
              {businessUnits?.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.name}
                </MenuItem>
              ))}
            </FormField>

            <Box>
              <Button
                onClick={() => setClientOpen((previous) => !previous)}
                endIcon={
                  <ExpandMore
                    sx={{
                      fontSize: 18,
                      transform: clientOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                    }}
                  />
                }
                sx={{
                  textTransform: 'none',
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  fontSize: 13,
                  color: NAVY,
                  borderRadius: RADIUS_ITEM,
                  px: 1,
                  '&:hover': { bgcolor: alpha(NAVY, 0.05) },
                }}
              >
                Datos del cliente
              </Button>

              <Collapse in={clientOpen}>
                <Box sx={{ mt: 1 }}>
                  <FormField
                    label="Perfil"
                    optional
                    multiline
                    minRows={4}
                    value={mockClientJson}
                    onChange={(e) => setMockClientJson(e.target.value)}
                    error={submitAttempted && !parsedMockClient.valid ? 'El JSON tiene un error de formato.' : null}
                    hint="Con este perfil se simula la conversación al correr el eval."
                    sx={{ '& .MuiOutlinedInput-input': { fontFamily: 'ui-monospace, monospace', fontSize: 12 } }}
                  />
                </Box>
              </Collapse>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, pt: 2 }}>
            <BackButton onClick={handleClose}>Cancelar</BackButton>
            <PrimaryButton
              onClick={handleSubmit}
              disabled={saving || source.loading}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: NAVY }} /> : undefined}
            >
              {saving ? 'Guardando…' : 'Guardar eval'}
            </PrimaryButton>
          </Box>
        </>
      )}
    </Drawer>
  );
};

export default CreateEvalPanel;
